import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { booksClient, errMsg } from './api';

const PricingPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('month'); // 'month' | 'year'
  const [loading, setLoading] = useState(null);
  const [stripeActive, setStripeActive] = useState(false);

  useEffect(() => {
    booksClient.get('/billing/plans')
      .then((r) => {
        setPlans(r.data.plans || []);
        setStripeActive(!!r.data.stripe_active);
      })
      .catch(() => setPlans([]));
    if (params.get('cancelled')) toast.error('Checkout cancelled');
  }, [params]);

  const priceOf = (p) => billing === 'year' ? (p.yearly_usd ?? p.monthly_usd * 12) : p.monthly_usd;
  const suffix = billing === 'year' ? '/yr' : '/mo';

  const onChoose = async (plan) => {
    if (plan.code === 'trial') {
      navigate('/books/signup');
      return;
    }
    const token = localStorage.getItem('books_token');
    if (!token) {
      navigate(`/books/signup?plan=${plan.code}&interval=${billing}`);
      return;
    }
    setLoading(plan.code);
    try {
      const { data } = await booksClient.post('/billing/checkout', {
        plan_code: plan.code,
        interval: billing,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050b12] text-slate-100" data-testid="books-pricing-page">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-emerald-500/20 blur-[120px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-20 border-b border-white/5 bg-[#050b12]/70 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/books" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 font-bold text-white">EB</div>
            <div className="text-[15px] font-semibold">Emerald Books</div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/books/login" className="text-sm text-slate-300 hover:text-white">Log in</Link>
            <Button onClick={() => navigate('/books/signup')} className="rounded-full bg-emerald-500 px-5 text-white hover:bg-emerald-400" data-testid="pricing-nav-signup">
              Start free
            </Button>
          </div>
        </div>
      </nav>

      <div className="relative z-10 mx-auto max-w-7xl px-6 pt-16 pb-24">
        <div className="text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-1.5 text-xs text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" /> Simple pricing. No surprises.
          </div>
          <h1 className="text-5xl font-semibold tracking-tight text-white sm:text-6xl">
            Pricing that scales with you.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
            Start free for 14 days. Upgrade when you're ready.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1">
            {['month', 'year'].map((b) => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                data-testid={`pricing-toggle-${b}`}
                className={
                  'rounded-full px-5 py-2 text-sm font-medium transition ' +
                  (billing === b ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:text-white')
                }
              >
                {b === 'month' ? 'Monthly' : 'Yearly · Save 20%'}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-4">
          {plans.map((p) => (
            <div
              key={p.code}
              data-testid={`pricing-plan-${p.code}`}
              className={
                'relative flex flex-col rounded-2xl border p-6 ' +
                (p.popular
                  ? 'border-emerald-400/40 bg-gradient-to-b from-emerald-500/10 to-transparent'
                  : 'border-white/10 bg-white/[0.02]')
              }
            >
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white">
                  Most popular
                </div>
              )}
              <div className="text-lg font-semibold text-white">{p.name}</div>
              <div className="mt-1 text-xs text-slate-400">{p.tagline}</div>
              <div className="mt-6">
                {p.monthly_usd === 0 ? (
                  <div className="text-4xl font-semibold text-white">Free</div>
                ) : (
                  <>
                    <span className="text-4xl font-semibold text-white">${priceOf(p)}</span>
                    <span className="text-sm text-slate-400">{suffix}</span>
                  </>
                )}
              </div>
              <Button
                disabled={loading === p.code}
                onClick={() => onChoose(p)}
                className={
                  'mt-6 h-11 w-full rounded-full font-semibold ' +
                  (p.popular
                    ? 'bg-emerald-500 text-white hover:bg-emerald-400'
                    : 'bg-white/10 text-white hover:bg-white/20')
                }
                data-testid={`pricing-cta-${p.code}`}
              >
                {loading === p.code ? 'Loading…' : p.cta} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {!stripeActive && (
          <div className="mt-12 rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 text-center text-sm text-amber-300" data-testid="pricing-stripe-inactive-banner">
            Billing is being configured. Sign up for the free trial today — you'll be able to pick a paid plan once activated.
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
